// ADR-0034 §5 + §6 — WP delimiter parse/serialize
//
// WP 의 block serialization 은 HTML 주석 형태:
//   <!-- wp:paragraph {"attr":1} -->
//   <p>본문</p>
//   <!-- /wp:paragraph -->
//
// Round-trip 룰 (codex v2 verdict #7 + v3 verdict #4):
//   - `_id` 는 export 시 strip (WP-side import 가 unknown attribute 로 인식해 깨짐 차단)
//   - `rawHtml` ↔ innerHTML
//   - 우리가 schema 정의 안 한 block = Tiptap opaque atom node (편집 불가, 보존)

import type { Block } from "./block";

/**
 * Block → WP delimiter 형식 직렬화 (export).
 * `_id` 와 `metadata.reusableRef` 는 strip — WP 호환.
 */
export function serializeBlock(block: Block): string {
  const exportable = sanitizeAttributesForExport(block);
  const attrsJson = serializeAttributes(exportable.attributes);
  const innerHtml = block.rawHtml ?? "";
  const innerSerialized = (block.innerBlocks ?? [])
    .map((b) => serializeBlock(b))
    .join("\n");

  // self-closing 형태 (innerHTML + innerBlocks 모두 없으면)
  if (!innerHtml && innerSerialized.length === 0) {
    return attrsJson
      ? `<!-- wp:${normalizeName(block.name)} ${attrsJson} /-->`
      : `<!-- wp:${normalizeName(block.name)} /-->`;
  }

  const open = attrsJson
    ? `<!-- wp:${normalizeName(block.name)} ${attrsJson} -->`
    : `<!-- wp:${normalizeName(block.name)} -->`;
  const close = `<!-- /wp:${normalizeName(block.name)} -->`;

  const body = [innerHtml, innerSerialized].filter(Boolean).join("\n");
  return `${open}\n${body}\n${close}`;
}

/** WP-side 에서 `core/` 는 보통 namespace 생략 (e.g. `<!-- wp:paragraph -->`) */
function normalizeName(name: string): string {
  return name.startsWith("core/") ? name.slice("core/".length) : name;
}

function serializeAttributes(attrs: Record<string, unknown>): string {
  const keys = Object.keys(attrs);
  if (keys.length === 0) return "";
  return JSON.stringify(attrs);
}

function sanitizeAttributesForExport(block: Block): {
  attributes: Record<string, unknown>;
} {
  // `_id` 와 `metadata.reusableRef` 제거. attributes 안의 임의 키는 그대로 export.
  return { attributes: { ...block.attributes } };
}
